// D:\DataBoltaHai\data-bolta-hai\data-bolta-hai\frontend\src\components\RBIChatbot.js
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './RBIChatbot.css';

function RBIChatbot() {
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Use environment variable for production API, fallback to localhost for local dev
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

  const SUGGESTED_CHIPS = [
    "Kisan Credit Card limits",
    "NPA classification criteria",
    "Basel III CET1 capital ratio requirement",
    "Counterfeit note procedure"
  ];

  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages.length, isLoading]);

  const handleSendMessage = async (textToSend) => {
    const queryText = (textToSend || inputMessage).trim();
    if (!queryText || isLoading) return;

    // 1. Add single user message
    const userMessage = { text: queryText, sender: 'user', id: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // History formatting
      const historyPayload = messages.slice(-4).map(m => ({ sender: m.sender, text: m.text }));

      // 2. Primary call: RBI Regulatory Policy Knowledge Base
      const policyRes = await fetch(`${API_BASE_URL}/api/policy-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: queryText, history: historyPayload }),
      });
      const policyData = policyRes.ok ? await policyRes.json() : null;

      let finalAnswer = '';
      let rbiSources = [];
      let webSources = [];

      const policySuccess = policyData && 
        policyData.answer && 
        !policyData.answer.includes("couldn't verify this");

      if (policySuccess) {
        finalAnswer = policyData.answer;
        rbiSources = policyData.sources || [];
      } else {
        // 3. Fallback 1: Bank Audit Guidance Notes
        const dataRes = await fetch(`${API_BASE_URL}/api/data-answer`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: queryText, history: historyPayload }),
        });
        const dataData = dataRes.ok ? await dataRes.json() : null;
        const dataSuccess = dataData && 
          dataData.answer && 
          !dataData.answer.includes("couldn't verify this");

        if (dataSuccess) {
          finalAnswer = dataData.answer;
          rbiSources = dataData.sources || [];
        } else {
          // 4. Fallback 2: Web Search (only if structured web_sources are actually returned)
          const webRes = await fetch(`${API_BASE_URL}/api/web-answer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ question: queryText, history: historyPayload }),
          });
          const webData = webRes.ok ? await webRes.json() : null;
          
          if (webData && webData.web_sources && webData.web_sources.length > 0) {
            finalAnswer = "Below are relevant web search results for your query:";
            webSources = webData.web_sources;
          } else {
            // Strictly enforce official fail-closed response when evidence is insufficient across all KBs
            finalAnswer = policyData?.answer || "I couldn't verify this from the available RBI information.";
          }
        }
      }

      // Cleanup citations from final answer
      finalAnswer = finalAnswer.replace(/\[\d+\]/g, '').replace(/【.*?】/g, '');

      // 5. Append ONE consolidated assistant message object
      const botMessage = {
        sender: 'bot',
        text: finalAnswer,
        rbiSources: rbiSources,
        webSources: webSources,
        id: Date.now() + 1
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'bot',
          text: "Unable to get a response right now. Please try again.",
          rbiSources: [],
          webSources: [],
          isError: true,
          id: Date.now() + 1
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };


  return (
    <div className="rbi-chatbot-page">
      <div className="chatbot-window">
        {/* Header */}
        <div className="chatbot-header">
          <div className="header-title-container">
            <div className="header-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
            <div>
              <h3 className="header-title">RBI Banking Assistant</h3>
              <div className="header-status">
                <span className="online-indicator"></span>
                <span className="status-text">Online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Message Container */}
        <div className="chatbot-messages" ref={chatContainerRef}>
          {messages.length === 0 ? (
            <div className="welcome-container">
              <div className="welcome-icon">🏦</div>
              <h4 className="welcome-title">RBI Banking Assistant</h4>
              <p className="welcome-desc">
                Ask questions about RBI Master Directions, Circulars, Basel III, IRACP, NPA, KCC, and banking guidelines.
              </p>
              <div className="suggestion-chips-container">
                {SUGGESTED_CHIPS.map((chip, idx) => (
                  <button
                    key={idx}
                    className="suggestion-chip"
                    onClick={() => handleSendMessage(chip)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className={`message-wrapper ${msg.sender}`}
              >
                <div className={`message ${msg.sender} ${msg.isError ? 'error-bubble' : ''}`}>
                  {msg.sender === 'user' ? (
                    <div className="user-message-content">{msg.text}</div>
                  ) : (
                    <div className="assistant-container">
                      {msg.text && (
                        <div className="markdown-body">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.text.replace(/<br\s*\/?>/gi, '\n')}
                          </ReactMarkdown>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          {isLoading && (
            <div className="message-wrapper bot">
              <div className="message bot loading-bubble">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Input Area */}
        <form onSubmit={handleSubmit} className="chatbot-input-form">
          <textarea
            className="chatbot-textarea"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about RBI banking, compliance, audits..."
            rows={1}
            disabled={isLoading}
          />
          <button
            type="submit"
            className="chatbot-send-btn"
            disabled={!inputMessage.trim() || isLoading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

export default RBIChatbot;
